import React from 'react';
import { ProxyTypeDropdown } from './ProxyTypeDropdown';
import type { ReturnsRangeInfo } from '../utils';
import type { FirstLayerProxyTypeValue } from 'venn-utils';
import type { FundToBulkProxy, FundToProxy } from '../../types';
import { useFirstLayerProxyTypeDropdownItems } from '../hooks/useProxyTypeDropdownItems';
import { Icon, Tooltip } from 'venn-ui-kit';

interface FirstLayerProxyTypeDropdownProps {
  value: FirstLayerProxyTypeValue | undefined;
  onSelect: (value: FirstLayerProxyTypeValue) => void;
  investments: (FundToProxy | FundToBulkProxy)[];
  rawInvestmentRanges: (ReturnsRangeInfo | null)[];
  selectedProxyRange: ReturnsRangeInfo | undefined;
  withExtrapolation: boolean;
  className?: string;
}

export const FirstLayerProxyTypeDropdown = ({
  value,
  onSelect,
  investments,
  rawInvestmentRanges,
  selectedProxyRange,
  withExtrapolation,
  className,
}: FirstLayerProxyTypeDropdownProps) => {
  const items = useFirstLayerProxyTypeDropdownItems({
    investments,
    rawInvestmentRanges,
    selectedProxyRange,
    withInvalidTypes: true,
    withExtrapolation,
  });

  const frequencies = investments.map(
    (investment, i) => rawInvestmentRanges[i]?.frequency ?? investment.returnFrequency,
  );

  const label = (
    <>
      Proxy Type
      {frequencies.some((frequency) => frequency !== 'DAILY') && (
        <Tooltip
          usePortal
          content={
            <>
              To add an additional proxy,
              <br />
              first select a Backfill proxy.
            </>
          }
        >
          <Icon className="ml-[3px] text-venn-grey-80" type="circle-info" prefix="far" />
        </Tooltip>
      )}
    </>
  );

  return (
    <ProxyTypeDropdown<FirstLayerProxyTypeValue>
      value={value}
      onSelect={onSelect}
      items={items}
      label={label}
      width={350}
      placeholder="Get started..."
      testId="qa-proxy-dropdown-menu"
      className={className}
    />
  );
};
